import { createClient as createSupabaseClient, SupabaseClient } from '@supabase/supabase-js';
import type { Database } from '@/integrations/supabase/types';

// Export URL for edge function calls
export const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY as string;

// Typed Supabase client
export type TypedSupabaseClient = SupabaseClient<Database>;

// Browser client for client components
export function createClient(): TypedSupabaseClient {
  return createSupabaseClient<Database>(SUPABASE_URL, SUPABASE_ANON_KEY, {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
    },
  });
}

// Suppress known non-critical unhandled rejections from third-party SDKs
if (typeof window !== 'undefined') {
  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason;

    // Supabase Auth Locks API SecurityError (incognito/privacy browsers)
    if (
      reason instanceof DOMException &&
      reason.name === 'SecurityError' &&
      reason.message?.includes('LockManager')
    ) {
      event.preventDefault();
      return;
    }

    // PostHog reloadFeatureFlags network error (non-critical, flags still work from cache)
    if (
      reason instanceof Error &&
      (reason.message?.includes('reloadFeatureFlags') ||
       reason.message?.includes('Failed to fetch') ||
       reason.stack?.includes('posthog'))
    ) {
      event.preventDefault();
      return;
    }
  });
}

// Singleton instance for convenience
let browserClient: TypedSupabaseClient | null = null;

export function getSupabaseClient(): TypedSupabaseClient {
  if (!browserClient) {
    browserClient = createClient();
  }
  return browserClient;
}

// Default export for convenience - lazily initialized singleton
export const supabase: TypedSupabaseClient = getSupabaseClient();
